import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowsClockwise, CurrencyCircleDollar, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { CURRENCIES } from "../lib/constants";
import { SectionHeader } from "../components/shared";
import { Button } from "../components/ui/button";

const NAMES = Object.fromEntries(CURRENCIES.map((c) => [c.code, c.label.split("— ")[1] || c.code]));

export default function RateBoard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["fx-rates"], queryFn: async () => (await api.get("/fx/rates")).data });

  const refresh = useMutation({
    mutationFn: async () => (await api.post("/fx/rates/refresh")).data,
    onSuccess: (d) => { qc.setQueryData(["fx-rates"], d); toast.success("Exchange rates refreshed from ECB"); },
    onError: (e) => toast.error(formatApiErrorDetail(e) || "Could not refresh rates"),
  });

  const rates = data?.rates_to_aud || {};
  const fetchedAt = data?.fetched_at ? new Date(data.fetched_at).toLocaleString() : "—";
  const rows = CURRENCIES.map((c) => ({ code: c.code, name: NAMES[c.code], rate: rates[c.code] }));

  return (
    <div className="animate-fade-up" data-testid="rateboard-page">
      <SectionHeader title="Exchange Rates" subtitle="Live mid-market rates to AUD, sourced from the European Central Bank (ECB)">
        <Button onClick={() => refresh.mutate()} disabled={refresh.isPending} data-testid="fx-refresh-button" className="rounded-sm bg-primary hover:bg-primary/90 h-9">
          <ArrowsClockwise size={16} weight="bold" className={`mr-1 ${refresh.isPending ? "animate-spin" : ""}`} /> Refresh Rates
        </Button>
      </SectionHeader>

      <div className="flex flex-wrap items-center gap-x-8 gap-y-2 mb-5 text-sm">
        <div><span className="overline-label block text-muted-foreground">ECB Rate Date</span><span className="font-mono font-semibold" data-testid="fx-rate-date">{data?.date || "—"}</span></div>
        <div><span className="overline-label block text-muted-foreground">Last Fetched</span><span className="tabular">{fetchedAt}</span></div>
        <div><span className="overline-label block text-muted-foreground">Base Currency</span><span className="font-mono font-semibold">AUD</span></div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 border-b border-border">
            <tr className="text-left">
              <th className="px-4 py-2.5 overline-label">Currency</th>
              <th className="px-4 py-2.5 overline-label">Code</th>
              <th className="px-4 py-2.5 overline-label text-right">1 unit = (AUD)</th>
              <th className="px-4 py-2.5 overline-label text-right">1 AUD = (unit)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.code} className="border-b border-border/60 last:border-0 hover:bg-secondary/20" data-testid={`fx-row-${r.code}`}>
                <td className="px-4 py-2.5 font-medium flex items-center gap-2"><CurrencyCircleDollar size={16} className="text-primary/70" /> {r.name}</td>
                <td className="px-4 py-2.5 font-mono">{r.code}</td>
                <td className="px-4 py-2.5 text-right tabular font-semibold" data-testid={`fx-rate-${r.code}`}>
                  {r.code === "AUD" ? "1.0000" : (r.rate ? r.rate.toFixed(4) : <span className="text-muted-foreground">unavailable</span>)}
                </td>
                <td className="px-4 py-2.5 text-right tabular text-muted-foreground">
                  {r.code === "AUD" ? "1.0000" : (r.rate ? (1 / r.rate).toFixed(4) : "—")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="flex items-start gap-2 mt-4 text-xs text-muted-foreground max-w-2xl">
        <Info size={15} className="shrink-0 mt-0.5" />
        Rates update automatically once per day. Purchase & sales documents lock in the rate at the moment they are created, so historical documents keep their original AUD conversion even after rates change.
      </p>
      {isLoading && <p className="mt-4 text-sm text-muted-foreground">Loading rates…</p>}
    </div>
  );
}
